import { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNotification } from '../../../../contexts/NotificationContext';
import { DRAFT_KEYS, loadDraft, hasDraft, clearDraft, saveDraft } from '../../../../utils/draftUtils';
import { 
  fetchPatientData, 
  fetchPostOpInstructions, 
  fetchNextIpNo,
  savePostOpInstructions, 
  updatePostOpInstructions,
  generateShareToken
} from '../api/postOpInstructionsApi';
import { mapBackendToForm, mapFormToBackend } from '../utils/mapping';
import { usePostOpInstructionsValidation } from './usePostOpInstructionsValidation';

export const usePostOpInstructions = (uhid_no, navigate) => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();
  
  const [success, setSuccess] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ open: false, message: "", severity: "success" });
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [currentSignatureRole, setCurrentSignatureRole] = useState("");
  const signaturePadRef = useRef(null);

  const [formData, setFormData] = useState({
    ip_no: "",
    uhid_no: uhid_no || "",
    pt_name: "",
    age: "",
    sex: "",
    ward_no: "",
    room_no: "",
    instruction_date: "",
    postoperative_day: "",
    surgery_done: "",
    anaesthesiologist: "",
    anaesthesia: "",
    position_of_patient: "",
    nil_per_oral: "",
    iv_fluids_advised: "",
    o2: "",
    oral_liquids: "",
    diet: "",
    motor_sensory_regression: "",
    antibiotic_advised: "",
    inj_pan_40mg_iv: "",
    tablet_pan_20_40mg: "",
    inj_emeset_4mg_slow_iv: "",
    inj_paracetamol_1gm_iv: "",
    inj_paracetamol_time: "",
    inj_paracetamol_days: "",
    inj_ukat: "",
    inj_ukat_time: "",
    inj_ukat_days: "",
    epidural_infusion: "",
    concentration: "",
    concentration_text: "",
    concentration_date_time: "",
    pain_analgesia_prescribed: "",
    pain_score: "",
    analgesia_prescribed: "",
    t_alprax_hs: "",
    t_alprax_dose: "",
    other_medications: "",
    cbg_insulin_orders: "",
    blood_investigations_details: "",
    blood_investigation_date: "",
    blood_investigation_time: "",
    blood_transfusion_plan: "",
    physiotherapy: "",
    opinion_or_review: "",
    queries_to_surgeon: "",
    intake_output: "",
    anaesthesiologist_signature_blob: "",
    anaesthesiologist_name: "",
    post_op_nurse_name_id: ""
  });

  const { errors, setErrors, validateField, validateForm } = usePostOpInstructionsValidation(formData);

  // Queries
  const { data: patientInfo } = useQuery({
    queryKey: ['patient', uhid_no],
    queryFn: () => fetchPatientData(uhid_no),
    enabled: !!uhid_no
  });

  const { data: nextIpData } = useQuery({
    queryKey: ['postOpNextIp'],
    queryFn: fetchNextIpNo,
    enabled: !success
  });

  const { data: existingData } = useQuery({
    queryKey: ['postOpInstructions', uhid_no],
    queryFn: () => fetchPostOpInstructions(uhid_no),
    enabled: !!uhid_no,
    retry: false
  });

  useEffect(() => {
    if (existingData && Object.keys(existingData).length > 0) {
      setSuccess(true);
      setFormData(prev => ({ ...prev, ...mapBackendToForm(existingData) }));
    }
  }, [existingData]);

  // Effects
  useEffect(() => {
    if (patientInfo) {
      setFormData(prev => ({ 
        ...prev, 
        pt_name: patientInfo.name,
        age: patientInfo.age || '',
        sex: patientInfo.sex || '',
      }));
    }
  }, [patientInfo]);

  useEffect(() => {
    if (nextIpData && !success) {
      setFormData(prev => ({ ...prev, ip_no: nextIpData.next_ip }));
    }
  }, [nextIpData, success]);

  // Draft loading and saving
  useEffect(() => {
    if (uhid_no && hasDraft(DRAFT_KEYS.POST_OPERATIVE_INSTRUCTIONS, uhid_no)) {
      const saved = loadDraft(DRAFT_KEYS.POST_OPERATIVE_INSTRUCTIONS, uhid_no);
      if (saved && saved.formData) {
        setFormData(prev => ({ ...prev, ...saved.formData }));
      }
    }
  }, [uhid_no]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (uhid_no) {
        saveDraft(DRAFT_KEYS.POST_OPERATIVE_INSTRUCTIONS, uhid_no, formData);
      }
    }, 2000);
    return () => clearTimeout(timeoutId);
  }, [formData, uhid_no]);

  // Handlers
  const handleInputChange = useCallback((event) => {
    const { name, value } = event.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  }, [errors, setErrors]);

  const handleBlur = useCallback((event) => {
    const { name, value } = event.target;
    validateField(name, value);
  }, [validateField]);

  // Signature logic
  const handleSignatureOpen = (role) => {
    setCurrentSignatureRole(role);
    setSignatureDialogOpen(true);
  };

  const handleSignatureClear = () => {
    signaturePadRef.current.clear();
  };

  const handleSignatureSave = () => {
    const dataURL = signaturePadRef.current.toDataURL();
    setFormData((prevData) => ({
      ...prevData,
      [currentSignatureRole]: dataURL.split(",")[1],
    }));
    setSignatureDialogOpen(false);
  };

  const handleSignatureClose = () => {
    setSignatureDialogOpen(false);
  };

  // Mutation
  const mutation = useMutation({
    mutationFn: (data) => {
      if (success) {
        return updatePostOpInstructions(uhid_no, data);
      } else {
        return savePostOpInstructions(data);
      }
    },
    onSuccess: () => {
      showNotification('success', success ? 'Instructions updated successfully!' : 'Instructions submitted successfully!');
      queryClient.invalidateQueries(['postOpInstructions', uhid_no]);
      clearDraft(DRAFT_KEYS.POST_OPERATIVE_INSTRUCTIONS, uhid_no);
      setTimeout(() => {
        navigate(`/mainPage/${uhid_no}`, {
          state: { from: 'PostOperativePhase', status: 'success', step: 3 },
        });
      }, 1000);
    },
    onError: (error) => {
      console.error('Submission error:', error);
      showNotification('error', 'Error in submitting instructions!');
    }
  });

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!validateForm()) return;
    mutation.mutate(mapFormToBackend(formData));
  };

  const handleShare = async () => {
    try {
      const res = await generateShareToken(uhid_no);
      const link = `${window.location.origin}/postOperativeForm2/${uhid_no}?token=${res.token}`;
      const message = `Dear Doctor,\n\nPlease fill the post-operative instructions:\n\n${link}`;
      await navigator.clipboard.writeText(link);
      setSubmitStatus({ open: true, message: "Secure link copied to clipboard!", severity: "info" });
      const phoneNumber = '+916379605575';
      window.open(`http://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`, '_blank');
    } catch (error) {
      console.error("Error generating share token:", error);
      setSubmitStatus({ open: true, message: "Failed to generate shareable link", severity: "error" });
    }
  };

  return {
    formData, setFormData,
    errors, setErrors,
    success,
    loading: patientInfo === undefined && nextIpData === undefined,
    submitStatus, setSubmitStatus,
    signatureDialogOpen, setSignatureDialogOpen,
    signaturePadRef,
    handleInputChange, handleBlur,
    handleSignatureOpen, handleSignatureClear, handleSignatureSave, handleSignatureClose,
    handleSubmit, handleShare,
    mutationLoading: mutation.isLoading
  };
};
